'use client'

import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormControl,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  Typography,
  Link,
  Snackbar,
  IconButton,
  CircularProgress,
} from '@mui/material'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { Close } from '@mui/icons-material'
import { isEmailValid } from 'utils'
import { type SsoProvider } from 'auth'
import {
  useAuth,
  useClerk,
  useSignIn,
  isClerkAPIResponseError,
  isEmailLinkError,
  EmailLinkErrorCodeStatus,
} from 'auth/clerk'
import * as Sentry from '@sentry/nextjs'
import { useRouter, useSearchParams } from 'next/navigation'
import { useAnnouncement } from 'ui/tools/announcer'
import { GoogleButton } from '@/components/GoogleButton'
import { InputField } from '@/components/InputField'
import { palette } from '@/components/providers/ThemeProvider/palette'

const MODE_VERIFY_EMAIL = 'verify-email'
const MODE_VERIFY_OAUTH = 'verify-oauth'

const NO_EMAIL_FOUND_MSG = 'Email not recognized'
const NO_TERMS_ACCEPTED_MSG =
  'You must accept our Terms of Use and Privacy Policy to continue.'
const GENERAL_ERROR_MSG =
  'An unknown error occurred. If the problem persists, please contact support.'
const EXPIRED_LINK_MSG =
  'Your sign-in link has expired. Enter your email below to get a new one.'
const CLIENT_MISMATCH_MSG =
  'For your security, sign-in links only work in the same browser where you asked for them. Open the email in your original browser, or enter your email below to start again here.'
const FAILED_LINK_MSG =
  "That sign-in link didn't work. Enter your email below to get a new one."

function isSessionExistsError(error: unknown): boolean {
  return (
    isClerkAPIResponseError(error) &&
    error.errors.some((e) => e.code === 'session_exists')
  )
}

// Known EmailLinkError codes are expected user outcomes, so they skip Sentry; null falls through to the generic error path.
function getEmailLinkErrorMessage(error: unknown): string | null {
  if (!(error instanceof Error) || !isEmailLinkError(error)) return null
  switch (error.code) {
    case EmailLinkErrorCodeStatus.Expired:
      return EXPIRED_LINK_MSG
    case EmailLinkErrorCodeStatus.ClientMismatch:
      return CLIENT_MISMATCH_MSG
    case EmailLinkErrorCodeStatus.Failed:
      return FAILED_LINK_MSG
    default:
      return null
  }
}

// Clerk's longMessage/message are usually user-presentable; surfacing them makes the unknown-error path debuggable without a Sentry round-trip.
function getClerkErrorDetail(error: unknown): string | null {
  if (isClerkAPIResponseError(error) && error.errors.length > 0) {
    const first = error.errors[0]
    return first.longMessage ?? first.message
  }
  if (error instanceof Error && error.message) return error.message
  return null
}

function GeneralErrorMessage({ detail }: { detail: string | null }) {
  return (
    <>
      <span>{GENERAL_ERROR_MSG}</span>
      {detail ? (
        <Typography
          variant="caption"
          component="span"
          sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}
        >
          {detail}
        </Typography>
      ) : null}
    </>
  )
}

function DividerWithOr() {
  return (
    <Divider>
      <Typography
        variant="caption"
        sx={{
          borderRadius: '50%',
          border: `1px solid ${palette.divider ?? 'gray'}`,
          width: 24,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        or
      </Typography>
    </Divider>
  )
}

export function Login({
  host,
  ssoProviders,
}: {
  host: string
  ssoProviders: SsoProvider[]
}) {
  const [isVerifying, setIsVerifying] = useState(false)
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null)
  const [workEmail, setWorkEmail] = useState('')
  const { signIn, isLoaded: isSignInLoaded } = useSignIn()
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isFormLoading, setIsFormLoading] = useState(false)
  const [isSsoLoading, setIsSsoLoading] = useState(false)
  const [isCallbackLoading, setIsCallbackLoading] = useState(false)
  const params = useSearchParams()
  const { isSignedIn } = useAuth()
  const router = useRouter()
  const { handleEmailLinkVerification, handleRedirectCallback, loaded } =
    useClerk()

  useEffect(() => {
    if (isSignedIn) {
      router.replace('/home')
    }
  }, [isSignedIn, router])

  const isAnyLoading = isGoogleLoading || isFormLoading || isSsoLoading

  const verifyEmail = useCallback(async () => {
    if (loaded) {
      try {
        setIsCallbackLoading(true)
        await handleEmailLinkVerification({
          redirectUrlComplete: `${host}/home`,
          redirectUrl: `${host}/sign-in`,
        })
      } catch (error: unknown) {
        const knownMessage = getEmailLinkErrorMessage(error)
        if (knownMessage) {
          setErrorMessage(knownMessage)
        } else {
          Sentry.captureException(error)
          setErrorMessage(
            <GeneralErrorMessage detail={getClerkErrorDetail(error)} />
          )
        }
      } finally {
        setIsCallbackLoading(false)
      }
    }
  }, [loaded, handleEmailLinkVerification, host])

  const verifyOAuth = useCallback(async () => {
    if (loaded) {
      try {
        setIsCallbackLoading(true)
        await handleRedirectCallback({
          transferable: false,
          signInUrl: `${host}/sign-in`,
          signInForceRedirectUrl: `${host}/home`,
        })
      } catch (error: unknown) {
        Sentry.captureException(error)
        setErrorMessage(
          <GeneralErrorMessage detail={getClerkErrorDetail(error)} />
        )
      } finally {
        setIsCallbackLoading(false)
      }
    }
  }, [loaded, handleRedirectCallback, host])

  useEffect(() => {
    if (params.get('mode') === MODE_VERIFY_EMAIL) {
      void verifyEmail()
    }
    if (params.get('mode') === MODE_VERIFY_OAUTH) {
      void verifyOAuth()
    }
    if (isSignInLoaded) {
      if (
        signIn.firstFactorVerification.error?.code ===
        'external_account_not_found'
      ) {
        setErrorMessage(NO_EMAIL_FOUND_MSG)
      }
    }
  }, [isSignInLoaded, signIn, params, verifyEmail, verifyOAuth])

  const isSigningIn =
    isGoogleLoading || isFormLoading || isSsoLoading || isCallbackLoading
  const announcement = isVerifying
    ? 'Check your email. An email has been sent with instructions to sign in.'
    : isSigningIn
      ? 'Signing in'
      : null
  useAnnouncement(announcement)

  if (!isSignInLoaded || isCallbackLoading)
    return (
      <CircularProgress
        color="primary"
        size="80px"
        aria-label="Loading sign-in"
      />
    )

  const onUseAnotherMethod = () => {
    setIsVerifying(false)
    setIsFormLoading(false)
  }

  const onGoogleClick = async () => {
    if (!hasAcceptedTerms) {
      setErrorMessage(NO_TERMS_ACCEPTED_MSG)
      return
    }

    try {
      setIsGoogleLoading(true)
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: `${host}/sign-in?mode=${MODE_VERIFY_OAUTH}`,
        redirectUrlComplete: `${host}/home`,
      })
    } catch (exception: unknown) {
      setIsGoogleLoading(false)
      setIsVerifying(false)
      if (isSessionExistsError(exception)) {
        router.replace('/home')
        return
      }
      Sentry.captureException(exception)
      setErrorMessage(
        <GeneralErrorMessage detail={getClerkErrorDetail(exception)} />
      )
    }
  }

  const onFormButtonClick = async () => {
    if (!hasAcceptedTerms) {
      setErrorMessage(NO_TERMS_ACCEPTED_MSG)
      return
    }
    if (!isEmailValid(workEmail)) {
      setErrorMessage('Please enter a valid email address.')
      return
    }

    try {
      setIsFormLoading(true)
      await signIn.create({
        strategy: 'email_link',
        identifier: workEmail,
        redirectUrl: `${host}/sign-in?mode=${MODE_VERIFY_EMAIL}`,
      })
      setIsVerifying(true)
    } catch (exception: unknown) {
      setIsVerifying(false)

      if (isSessionExistsError(exception)) {
        router.replace('/home')
        return
      }
      if (exception instanceof Error) {
        const message = exception.message
        if (message === "Couldn't find your account.") {
          setErrorMessage(NO_EMAIL_FOUND_MSG)
          return
        }
      }
      Sentry.captureException(exception)
      setErrorMessage(
        <GeneralErrorMessage detail={getClerkErrorDetail(exception)} />
      )
    } finally {
      setIsFormLoading(false)
    }
  }

  const onSsoSelect = async (event: SelectChangeEvent) => {
    const providerKey = event.target.value
    if (!providerKey) return

    if (!hasAcceptedTerms) {
      setErrorMessage(NO_TERMS_ACCEPTED_MSG)
      return
    }

    try {
      setIsSsoLoading(true)
      await signIn.authenticateWithRedirect({
        strategy: `oauth_custom_${providerKey}`,
        redirectUrl: `${host}/sign-in?mode=${MODE_VERIFY_OAUTH}`,
        redirectUrlComplete: `${host}/home`,
      })
    } catch (exception: unknown) {
      setIsSsoLoading(false)
      if (isSessionExistsError(exception)) {
        router.replace('/home')
        return
      }
      Sentry.captureException(exception)
      setErrorMessage(
        <GeneralErrorMessage detail={getClerkErrorDetail(exception)} />
      )
    }
  }

  return (
    <>
      <Stack spacing={3} sx={{ alignItems: 'center' }}>
        <Paper elevation={12} sx={{ width: 400, p: 4 }}>
          <Stack spacing={2.5}>
            {isVerifying ? (
              <Stack spacing={2}>
                <Typography variant="h4">Check your email</Typography>
                <Typography>
                  An email has been sent to <strong>{workEmail}</strong> with
                  instructions to sign in. You can now close this tab.
                </Typography>
                <Link onClick={onUseAnotherMethod} href="#">
                  Use another method
                </Link>
              </Stack>
            ) : (
              <>
                <Typography variant="h4" fontWeight={700} textAlign="center">
                  Sign In
                </Typography>
                <GoogleButton
                  onClick={onGoogleClick}
                  disabled={isFormLoading || isSsoLoading}
                  loading={isGoogleLoading}
                  sx={{ justifyContent: 'center' }}
                >
                  Sign in with Google
                </GoogleButton>
                <DividerWithOr />
                <InputField
                  placeholder="School or work email"
                  value={workEmail}
                  disabled={isAnyLoading}
                  onChange={(e) => {
                    setWorkEmail(e.target.value)
                  }}
                  sx={{ backgroundColor: 'white' }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={onFormButtonClick}
                  loading={isFormLoading}
                  disabled={isGoogleLoading || isSsoLoading}
                >
                  Send me a link to sign in
                </Button>
                {ssoProviders.length > 0 && (
                  <>
                    <DividerWithOr />
                    <FormControl fullWidth>
                      <Select
                        displayEmpty
                        value=""
                        onChange={onSsoSelect}
                        disabled={isAnyLoading}
                        size="small"
                        startAdornment={
                          isSsoLoading ? (
                            <CircularProgress
                              size={20}
                              aria-label="Signing in"
                            />
                          ) : null
                        }
                        renderValue={() => (
                          <Typography color="text.disabled" sx={{ pl: 1 }}>
                            Sign in through your org
                          </Typography>
                        )}
                      >
                        {ssoProviders.map((provider) => (
                          <MenuItem key={provider.key} value={provider.key}>
                            {provider.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
                <Divider />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasAcceptedTerms}
                      onChange={(e) => {
                        setHasAcceptedTerms(e.target.checked)
                      }}
                    />
                  }
                  label={
                    <>
                      I agree to the PBLWorks{' '}
                      <Link
                        href="https://www.pblworks.org/terms#terms-of-use"
                        target="_blank"
                      >
                        Terms of Use
                      </Link>{' '}
                      and{' '}
                      <Link
                        href="https://www.pblworks.org/terms#privacy-policy"
                        target="_blank"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </>
                  }
                />
              </>
            )}
          </Stack>
        </Paper>
      </Stack>
      <Snackbar
        open={Boolean(errorMessage)}
        onClose={() => {
          setErrorMessage(null)
        }}
        message={errorMessage}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              setErrorMessage(null)
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />
    </>
  )
}
